import { useState } from 'react';
import type { Yacht } from '../../../mocks/yachtListings';

interface FilterBarProps {
  yachts: Yacht[];
  onFilter: (filtered: Yacht[]) => void;
}

export default function FilterBar({ yachts, onFilter }: FilterBarProps) {
  const [priceRange, setPriceRange] = useState<string>('all');
  const [lengthRange, setLengthRange] = useState<string>('all');
  const [builder, setBuilder] = useState<string>('all');

  const applyFilters = (price: string, length: string, builderFilter: string) => {
    let filtered = [...yachts];

    // Price filter
    if (price !== 'all') {
      const [min, max] = price.split('-').map(Number);
      filtered = filtered.filter(yacht => {
        if (max) {
          return yacht.price >= min * 1000000 && yacht.price <= max * 1000000;
        }
        return yacht.price >= min * 1000000;
      });
    }

    // Length filter
    if (length !== 'all') {
      const [min, max] = length.split('-').map(Number);
      filtered = filtered.filter(yacht => {
        if (max) {
          return yacht.length >= min && yacht.length <= max;
        }
        return yacht.length >= min;
      });
    }

    // Builder filter
    if (builderFilter !== 'all') {
      filtered = filtered.filter(yacht => yacht.builder === builderFilter);
    }

    onFilter(filtered);
  };

  const handlePriceChange = (value: string) => {
    setPriceRange(value);
    applyFilters(value, lengthRange, builder);
  };

  const handleLengthChange = (value: string) => {
    setLengthRange(value);
    applyFilters(priceRange, value, builder);
  };

  const handleBuilderChange = (value: string) => {
    setBuilder(value);
    applyFilters(priceRange, lengthRange, value);
  };

  const resetFilters = () => {
    setPriceRange('all');
    setLengthRange('all');
    setBuilder('all');
    onFilter(yachts);
  };

  const builders = Array.from(new Set(yachts.map(y => y.builder))).sort();

  return (
    <div className="bg-gray-50 border-y border-gray-200 py-8 px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Price Range */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">Price Range</label>
            <select
              value={priceRange}
              onChange={(e) => handlePriceChange(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-gray-300 text-base text-black focus:outline-none focus:border-black cursor-pointer"
            >
              <option value="all">All Prices</option>
              <option value="20-50">$20M - $50M</option>
              <option value="50-75">$50M - $75M</option>
              <option value="75-100">$75M - $100M</option>
              <option value="100">$100M+</option>
            </select>
          </div>

          {/* Length Range */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">Length</label>
            <select
              value={lengthRange}
              onChange={(e) => handleLengthChange(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-gray-300 text-base text-black focus:outline-none focus:border-black cursor-pointer"
            >
              <option value="all">All Lengths</option>
              <option value="60-70">60m - 70m</option>
              <option value="70-80">70m - 80m</option>
              <option value="80-90">80m - 90m</option>
              <option value="90">90m+</option>
            </select>
          </div>

          {/* Builder */}
          <div>
            <label className="block text-sm text-gray-600 mb-2">Builder</label>
            <select
              value={builder}
              onChange={(e) => handleBuilderChange(e.target.value)}
              className="w-full px-4 py-3 bg-white border border-gray-300 text-base text-black focus:outline-none focus:border-black cursor-pointer"
            >
              <option value="all">All Builders</option>
              {builders.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          {/* Reset Button */}
          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="w-full px-4 py-3 bg-black text-white text-base hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
